import React, { useEffect } from 'react';
import { Formik } from 'formik';
import { Stack } from '@chakra-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { sub } from 'date-fns';

import { TabForm } from '../../common/components/Tabs';
import { FormSection, FormActions } from '../../common/components/Form/Form';
import { PrimaryButton } from '../../common/components/Buttons';
import { SelectInput } from '../../common/components/Form/FormSelectInput';
import { DateInput } from '../../common/components/Form/FormDateInput';
import { tagsAPI } from '../../common/api/tags';
import { getDateString } from '../../common/utils';
import { usersAPI } from '../../common/api/users';
import { toastActions } from '../../redux/toast/actions';
import { configActions } from '../../redux/config/actions';
import rc from './constants';

// Sentinel option values for the "select all" entries. Reserved — do not use as real
// tag or department codes. Picked with leading/trailing underscores to avoid collision
// with backend-supplied values (which never include underscore-bracketed tokens).
const ALL_TAGS_OPTION = { label: 'Tous les tags', value: '__all_tags__' };
const ALL_DEPARTMENTS_OPTION = { label: 'Tous les départements', value: '__all_departments__' };

const mapValuesToOptions = values => (values || []).map(value => ({ label: value, value }));

const withoutSelectAllOption = (values, selectAllOption) =>
  (values || []).filter(value => value.value !== selectAllOption.value);

const buildArrayQueryParams = (
  paramName,
  values,
  selectAllOption,
  valueMapper = value => value.value,
) =>
  withoutSelectAllOption(values, selectAllOption)
    .map(value => `${paramName}[]=${encodeURIComponent(valueMapper(value))}`)
    .join('&');

const getDepartmentCode = department => department.value.split(' - ')[0];

export const ReportingForm = ({ mode = rc.OPERATORS_REPORTING }) => {
  const user = useSelector(state => state.authentication.user);
  const validDepartments = useSelector(state => state.config.validDepartments || []);
  const dispatch = useDispatch();

  const displayTagInput = [
    rc.OPERATORS_REPORTING,
    rc.FOLDERS_REPORTING,
    rc.FOLDER_LIST_REPORTING,
  ].includes(mode);
  const displayDepartmentInput = mode === rc.FOLDER_LIST_REPORTING;
  const displaySingleDateInput = [rc.OPERATORS_REPORTING].includes(mode);
  const displayDoubleDateInput = [rc.FOLDERS_REPORTING, rc.FOLDER_LIST_REPORTING].includes(mode);

  useEffect(() => {
    if (displayDepartmentInput && !validDepartments.length) {
      dispatch(configActions.getConfig());
    }
  }, [dispatch, displayDepartmentInput, validDepartments.length]);

  const titles = {
    [rc.OPERATORS_REPORTING]: 'Activité opérateurs',
    [rc.FOLDERS_REPORTING]: 'Activité dossiers',
    [rc.ERGOS_REPORTING]: 'Ergothératpeutes présents par département',
    [rc.FOLDER_LIST_REPORTING]: 'Liste des dossiers',
  };

  const validateDoubleDate = values => {
    const errors = {};
    if (mode === rc.FOLDERS_REPORTING && !values.startDate && !values.endDate) {
      errors.startDate = ["n'est pas rempli(e)"];
      errors.endDate = ["n'est pas rempli(e)"];
    }
    return errors;
  };

  const validateSingleDate = values => {
    const errors = {};
    if (!values.date) errors.date = ["n'est pas rempli(e)"];
    return errors;
  };

  const validate = values =>
    ({
      [rc.OPERATORS_REPORTING]: validateSingleDate(values),
      [rc.FOLDERS_REPORTING]: validateDoubleDate(values),
      [rc.FOLDER_LIST_REPORTING]: validateDoubleDate(values),
      [rc.ERGOS_REPORTING]: validateSingleDate(values),
    }[mode]);

  const getInitialValues = () => {
    if (mode === rc.FOLDERS_REPORTING || mode === rc.FOLDER_LIST_REPORTING)
      return {
        startDate: sub(new Date(), { months: 1 }),
        endDate: new Date(),
        tags: [],
        departments: [],
      };
    return { date: new Date(), tags: [] }; // default
  };

  const getAvailableTags = inputValue =>
    user.role === 'pilote externe'
      ? usersAPI.getUser(user.id).then(({ tags }) => mapValuesToOptions(tags))
      : tagsAPI
          .getTags({ search: inputValue || '' })
          .then(tags => mapValuesToOptions(tags));

  const getTags = inputValue =>
    getAvailableTags(inputValue).then(tags => [ALL_TAGS_OPTION, ...tags]);

  const departmentOptions = mapValuesToOptions(validDepartments);

  const handleTagsChange = (selectedTags, formik) => {
    if (selectedTags?.some(tag => tag.value === ALL_TAGS_OPTION.value)) {
      getAvailableTags('').then(tags => formik.setFieldValue('tags', tags));
    }
  };

  const handleDepartmentsChange = (selectedDepartments, formik) => {
    if (selectedDepartments?.some(department => department.value === ALL_DEPARTMENTS_OPTION.value)) {
      formik.setFieldValue('departments', [ALL_DEPARTMENTS_OPTION]);
    }
  };

  const getTagsQueryParams = values => buildArrayQueryParams('tags', values.tags, ALL_TAGS_OPTION);

  const getDepartmentsQueryParams = values =>
    buildArrayQueryParams(
      'departments',
      values.departments,
      ALL_DEPARTMENTS_OPTION,
      getDepartmentCode,
    );

  const getQueryString = params => params.filter(param => param).join('&');

  const getDownloadUrl = values =>
    ({
      [rc.FOLDERS_REPORTING]: `${
        process.env.REACT_APP_API_URL
      }/api/v1/reportings/${mode}_export?start_date=${getDateString(
        values.startDate,
      )}&end_date=${getDateString(values.endDate)}&${getTagsQueryParams(values)}`,
      [rc.FOLDER_LIST_REPORTING]: `${
        process.env.REACT_APP_API_URL
      }/api/v1/reportings/${mode}_export?${getQueryString([
        `start_date=${getDateString(values.startDate)}`,
        `end_date=${getDateString(values.endDate)}`,
        getTagsQueryParams(values),
        getDepartmentsQueryParams(values),
      ])}`,
      [rc.OPERATORS_REPORTING]: `${
        process.env.REACT_APP_API_URL
      }/api/v1/reportings/${mode}_export?date=${getDateString(
        values.date,
      )}&${getTagsQueryParams(values)}`,
      [rc.ERGOS_REPORTING]: `${process.env.REACT_APP_API_URL}/api/v1/reportings/${mode}_export`,
    }[mode]);

  return (
    <Formik
      initialValues={getInitialValues()}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(values, { setSubmitting, setErrors, setTouched }) => {
        setSubmitting(true);
        const errors = validate(values);

        if (errors && Object.keys(errors).length === 0 && errors.constructor === Object) {
          window.open(getDownloadUrl(values));
        } else {
          setErrors(errors);
          dispatch(toastActions.error('Certains champs du formulaire sont manquants'));
          setTouched({});
        }

        setSubmitting(false);
      }}
    >
      {formik => (
        <TabForm onSubmit={formik.handleSubmit}>
          <Stack spacing={10}>
            <FormSection title={titles[mode]}>
              {displayDoubleDateInput && (
                <>
                  <DateInput label="Date de début" name="startDate" id="startDate" />
                  <DateInput label="Date de fin" name="endDate" id="endDate" />
                </>
              )}
              {displaySingleDateInput && <DateInput label="Date" name="date" id="date" />}
              {displayTagInput && (
                <SelectInput
                  mt="2.8rem"
                  css={{
                    '[class*="-option"], [class*="-ValueContainer"]': {
                      textTransform: 'uppercase',
                    },
                    '[class*="-placeholder"]': { textTransform: 'none' },
                  }}
                  placeholder="Choisir un tag"
                  label="Tags"
                  name="tags"
                  id="tags"
                  defaultOptions
                  cacheOptions
                  isAsync
                  isMulti
                  isUpperCase
                  noOptionsMessage={() => 'Aucun tag ne correspond à cette recherche'}
                  loadingMessage={() => 'Recherche en cours...'}
                  loadOptions={getTags}
                  disabled={formik.isSubmitting}
                  closeMenuOnSelect={false}
                  postOnChange={selectedTags => handleTagsChange(selectedTags, formik)}
                />
              )}
              {displayDepartmentInput && (
                <SelectInput
                  mt="2.8rem"
                  placeholder="Choisir un département"
                  label="Départements"
                  name="departments"
                  id="departments"
                  options={[ALL_DEPARTMENTS_OPTION, ...departmentOptions]}
                  isMulti
                  noOptionsMessage={() => 'Aucun département ne correspond à cette recherche'}
                  disabled={formik.isSubmitting}
                  closeMenuOnSelect={false}
                  postOnChange={selectedDepartments =>
                    handleDepartmentsChange(selectedDepartments, formik)
                  }
                />
              )}
            </FormSection>

            <FormActions mt={0}>
              <PrimaryButton
                pos="relative!important"
                transform="none!important"
                right="0!important"
                type="submit"
                isLoading={formik.isSubmitting}
                loadingText="Génération en cours"
              >
                Télécharger le rapport
              </PrimaryButton>
            </FormActions>
          </Stack>
        </TabForm>
      )}
    </Formik>
  );
};
